import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Input,
  Chip,
  Tooltip,
  Badge,
  TablePagination,
  Select,
  MenuItem
} from '@mui/material';
import { Add, Edit, Delete, CloudUpload } from '@material-ui/icons';
import { withSnackbar } from 'notistack';
import MasterCategoryService from './Services/MasterCategoryService'; // Import the service
import './MasterCategory.css'; // Import the CSS file
import { masterCategoryTranslations } from './MasterCategoryTranslations';

const MasterCategory = ({ enqueueSnackbar }) => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [formData, setFormData] = useState({
    language_name: '',
    product_id: '',
    category_name: '',
    category_text: '',
    category_id: '',
    cg_thumbnail: null,
    display_order: '',
    adult: false,
    carousel_cid: '',
    recent_video_cid: '',
    is_short: false,
    shorts_cid_context_mapping: '',
    circular_category_thumbnail: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [circularThumbnailPreview, setCircularThumbnailPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [progress, setProgress] = useState({ active: false, count: 0, total: 0 });
  const fileInputRef = React.useRef(null);

  const languageOptions = [
    { label: 'English', code: 'en' },
    { label: 'Arabic', code: 'ar' },
    { label: 'French', code: 'fr' },
    { label: 'Portuguese', code: 'pt' },
    { label: 'Thai', code: 'th' },
    { label: 'Khmer', code: 'km' },
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase();

    const filteredData = categories.filter(item => {
      const languageLabel =
          languageOptions
              .find(option => option.code === item.languageName)
              ?.label
              .toLowerCase() || '';

      const matchesObjectFields = Object.keys(item).some(key => {
        const value = item[key];

        if (value === null || value === undefined) {
          return false;
        }

        if (Array.isArray(value)) {
          return value.some(subItem =>
              getRenderableValue(subItem)
                  .toLowerCase()
                  .includes(lowercasedFilter)
          );
        }

        return getRenderableValue(value)
            .toLowerCase()
            .includes(lowercasedFilter);
      });

      return matchesObjectFields ||
          languageLabel.includes(lowercasedFilter);
    });

    setFilteredCategories(filteredData);
  }, [searchTerm, categories]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await MasterCategoryService.fetchCategories();
      setCategories(data);
      setFilteredCategories(data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      enqueueSnackbar(masterCategoryTranslations.categoriesLoadedError, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const openForm = (category = null) => {
    if (category) {
      setFormData({
        language_name: category.languageName,
        product_id: category.productId,
        category_name: category.categoryName,
        category_text: category.categoryText,
        category_id: category.categoryId.join(','),
        cg_thumbnail: category.cgThumbnail,
        display_order: category.displayOrder,
        adult: category.adult?.toLowerCase() === 'yes',
        carousel_cid: category.carouselCid.join(','),
        recent_video_cid: category.recentVideoCid.join(','),
        is_short:
            category.isShort === 1 ||
            category.isShort === true ||
            category.isShort === '1',
        shorts_cid_context_mapping: getRenderableValue(category.shortsCidContextMapping),
        circular_category_thumbnail:
            typeof category.circularCategoryThumbnail === 'string'
                ? category.circularCategoryThumbnail
                : '',
      });
      setImagePreview(category.cgThumbnail);
      setCircularThumbnailPreview(
          typeof category.circularCategoryThumbnail === 'string'
              ? category.circularCategoryThumbnail
              : null
      );
      setCurrentCategory(category);
    } else {
      setFormData({
        language_name: '', product_id: '', category_name: '', category_text: '', category_id: '', cg_thumbnail: null,
        display_order: '', adult: false, carousel_cid: '', recent_video_cid: '',is_short: false,shorts_cid_context_mapping: '',circular_category_thumbnail: null
      });
      setImagePreview(null);
      setCircularThumbnailPreview(null);
      setCurrentCategory(null);
    }
    setErrors({});
    setOpenDialog(true);
  };

  const closeForm = () => setOpenDialog(false);

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setErrors(prev => ({ ...prev, [name]: '' })); // Clear error on change
  };

  const handleIsShortChange = (e) => {
    const checked = e.target.checked;

    setFormData(prev => ({
      ...prev,
      is_short: checked,

      ...(checked
          ? {
            carousel_cid: '',
            recent_video_cid: ''
          }
          : {
            shorts_cid_context_mapping: '',
            circular_category_thumbnail: null
          })
    }));

    if (!checked) {
      setCircularThumbnailPreview(null);
    }
  };
  const handleFileChange = e => {
    const file = e.target.files[0];
    setFormData(prev => ({ ...prev, cg_thumbnail: file }));
    setImagePreview(URL.createObjectURL(file));
    setErrors(prev => ({ ...prev, cg_thumbnail: '' })); // Clear error on change
  };

  const handleCircularThumbnailChange = e => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    setFormData(prev => ({
      ...prev,
      circular_category_thumbnail: file
    }));

    setCircularThumbnailPreview(URL.createObjectURL(file));

    setErrors(prev => ({
      ...prev,
      circular_category_thumbnail: ''
    }));
  };

  const validateForm = () => {
    const requiredFields = ['language_name', 'product_id', 'category_name', 'category_text', 'category_id', 'display_order', 'cg_thumbnail'];
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (requiredFields.includes(key) && !value) {
        newErrors[key] = `Please fill out the ${key.replace('_', ' ')} field.`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      if (currentCategory) {
        console.log("update Category Form", formData);
        await MasterCategoryService.updateCategory(currentCategory.masterCategoryId, formData);
        enqueueSnackbar(masterCategoryTranslations.categoryUpdatedSuccess, { variant: 'success' });
      } else {
        console.log("create Category Form", formData);
        await MasterCategoryService.createCategory(formData);
        enqueueSnackbar(masterCategoryTranslations.categoryAddedSuccess, { variant: 'success' });
      }
      fetchCategories();
      closeForm();
    } catch (err) {
      console.error('Error saving category:', err);
      enqueueSnackbar(masterCategoryTranslations.categorySaveError, { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const pushToProd = async (categoryId, productId, languageName) => {
    try {
      await MasterCategoryService.pushToProd(categoryId, productId, languageName);
      //   enqueueSnackbar('Category pushed to production successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error pushing category to production:', err);
      enqueueSnackbar('Failed to push category to production', { variant: 'error' });
    }
  };

  const confirmDeleteCategory = (categoryId) => {
    setCategoryToDelete(categoryId);
    setDeleteDialogOpen(true);
  };

  const deleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await MasterCategoryService.deleteCategory(categoryToDelete, 'user@example.com'); // Replace with actual email
      enqueueSnackbar(masterCategoryTranslations.categoryDeletedSuccess, { variant: 'success' });
      fetchCategories();
    } catch (err) {
      console.error('Error deleting category:', err);
      enqueueSnackbar(masterCategoryTranslations.categoryDeleteError, { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const calculateDisplayLimit = (ids) => {
    let width = 0;
    let limit = 0;
    for (let id of ids) {
      width += id.length * 8; // Approximate width per character
      if (width > 100) break;
      limit++;
    }
    return Math.max(limit, 1);
  };


  const getRenderableValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (
        typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean'
    ) {
      return String(value);
    }

    if (Array.isArray(value)) {
      return value
          .map(item => getRenderableValue(item))
          .join(', ');
    }

    if (typeof value === 'object') {
      return Object.entries(value)
          .map(([key, val]) => `${key}:${getRenderableValue(val)}`)
          .join(', ');
    }

    return String(value);
  };

  const normalizeIds = (ids) => {
    if (Array.isArray(ids)) {
      return ids.map(id => getRenderableValue(id));
    }

    if (ids === null || ids === undefined || ids === '') {
      return [];
    }

    if (typeof ids === 'string') {
      return ids
          .split(',')
          .map(id => id.trim())
          .filter(Boolean);
    }

    return [getRenderableValue(ids)];
  };

  const renderIdsWithTooltip = (ids) => {
    const safeIds = normalizeIds(ids);

    if (safeIds.length === 0) {
      return '-';
    }

    const displayLimit = calculateDisplayLimit(safeIds);
    const displayIds = safeIds.slice(0, displayLimit);
    const remainingCount = safeIds.length - displayLimit;

    return (
        <Tooltip title={safeIds.join(', ')}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: '14px' }}>
          {displayIds.join(', ')}
        </span>

        {remainingCount > 0 && (
            <Badge
                badgeContent={`+${remainingCount}`}
                color="secondary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    fontWeight: 'bold',
                    height: '16px',
                    minWidth: '16px',
                  }
                }}
            />
        )}
      </span>
        </Tooltip>
    );
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allSelectedCategoryIds = filteredCategories.map((n) => n.masterCategoryId);
      setSelectedCategories(allSelectedCategoryIds);
      return;
    }
    setSelectedCategories([]);
  };

  const handleClick = (event, id) => {
    let newSelected = [];

    if (!Array.isArray(selectedCategories)) {
      console.error('selectedCategories is not an array');
      return;
    }

    if (!selectedCategories.includes(id)) {
      newSelected = newSelected.concat(selectedCategories, id);
    } else {
      const selectedIndex = selectedCategories.indexOf(id);
      if (selectedIndex === 0) {
        newSelected = newSelected.concat(selectedCategories.slice(1));
      } else if (selectedIndex === selectedCategories.length - 1) {
        newSelected = newSelected.concat(selectedCategories.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
            selectedCategories.slice(0, selectedIndex),
            selectedCategories.slice(selectedIndex + 1)
        );
      }
    }

    setSelectedCategories(newSelected);
  };

  const isSelected = (id) => selectedCategories.indexOf(id) !== -1;

  const pushSelectedToProd = async () => {
    // Check for duplicate values in selected categories
    const selectedCategoriesData = selectedCategories.map(id => categories.find(c => c.masterCategoryId === id));
    const duplicateCheck = new Set();
    const duplicates = selectedCategoriesData.some(category => {
      const identifier = `${category.productId}-${category.languageName}-${category.categoryName}-${category.categoryText}-${category.displayOrder}`; // Example identifier with multiple fields
      if (duplicateCheck.has(identifier)) {
        return true;
      }
      duplicateCheck.add(identifier);
      return false;
    });

    if (duplicates) {
      enqueueSnackbar('Duplicate values found in selected categories. Please correct them before proceeding.', { variant: 'error' });
      return;
    }

    setProgress({ active: true, count: 0, total: selectedCategories.length });
    for (let i = 0; i < selectedCategories.length; i++) {
      try {
        const category = categories.find(c => c.masterCategoryId === selectedCategories[i]);
        await pushToProd(selectedCategories[i], category.productId, category.languageName);
        setProgress((prev) => ({ ...prev, count: prev.count + 1 }));
      } catch (err) {
        console.error('Error pushing category to production:', err);
        enqueueSnackbar(`${masterCategoryTranslations.failedToPushCategory} ID ${selectedCategories[i]}`, { variant: 'error' });
      }
    }
    setProgress({ active: false, count: 0, total: 0 });
    enqueueSnackbar(masterCategoryTranslations.selectedCategoriesPushed, { variant: 'success' });
    setSelectedCategories([]);
    fetchCategories(); // Refresh categories after push operation
  };

  const renderShimmerRows = () => {
    return Array.from({ length: 10 }).map((_, index) => (
        <TableRow key={index} className="shimmer-row">
          <TableCell colSpan={10}>
            <div className="shimmer-wrapper">
              <div className="shimmer" />
            </div>
          </TableCell>
        </TableRow>
    ));
  };

  return (
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">{masterCategoryTranslations.masterCategoriesTitle}</Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
                variant="outlined"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder={masterCategoryTranslations.searchPlaceholder}
                sx={{
                  width: '300px', // Set a specific width or use a percentage like '50%'
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '25px',
                    backgroundColor: '#f5f5f5',
                    '& fieldset': {
                      borderColor: '#ddd',
                    },
                    '&:hover fieldset': {
                      borderColor: '#aaa',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#3f51b5',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '10px 15px',
                  },
                }}
            />
            <Button
                variant="contained"
                color="primary"
                onClick={() => progress.active ? null : pushSelectedToProd()}
                disabled={selectedCategories.length === 0}
            >
              {progress.active ? `Pushing ${progress.count}/${progress.total}` : masterCategoryTranslations.pushToProdButton}
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => openForm()}>
              {masterCategoryTranslations.addCategoryButton}
            </Button>
          </Box>
        </Box>

        {loading ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                          indeterminate={selectedCategories.length > 0 && selectedCategories.length < filteredCategories.length}
                          checked={filteredCategories.length > 0 && selectedCategories.length === filteredCategories.length}
                          onChange={handleSelectAllClick}
                      />
                    </TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Language</TableCell>
                    <TableCell>Category Name</TableCell>
                    <TableCell>Category Text</TableCell>
                    <TableCell>Thumbnail</TableCell>
                    <TableCell>Display Order</TableCell>
                    <TableCell>Adult</TableCell>
                    <TableCell>Carousel CIDs</TableCell>
                    <TableCell>Recent Video CIDs</TableCell>
                    <TableCell>Is Short</TableCell>
                    <TableCell>Shorts CID Context Mapping</TableCell>
                    <TableCell>Circular Thumbnail</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderShimmerRows()}
                </TableBody>
              </Table>
              <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredCategories.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
        ) : (
            <Box>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                            indeterminate={selectedCategories.length > 0 && selectedCategories.length < filteredCategories.length}
                            checked={filteredCategories.length > 0 && selectedCategories.length === filteredCategories.length}
                            onChange={handleSelectAllClick}
                        />
                      </TableCell>
                      <TableCell>ID</TableCell>
                      <TableCell>Language</TableCell>
                      <TableCell>Product ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Text</TableCell>
                      <TableCell className="text-overflow">Category ID</TableCell>
                      <TableCell>Thumbnail</TableCell>
                      <TableCell>Order</TableCell>
                      <TableCell>Adult</TableCell>
                      <TableCell className="text-overflow">Carousel CID</TableCell>
                      <TableCell className="text-overflow">Recent CID</TableCell>
                      <TableCell>Is Short</TableCell>
                      <TableCell>Shorts CID Context Mapping</TableCell>
                      <TableCell>Circular Thumbnail</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCategories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={15} align="center">
                            No results found for your search.
                          </TableCell>
                        </TableRow>
                    ) : (
                        filteredCategories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((cat) => {
                          const isItemSelected = isSelected(cat.masterCategoryId);
                          return (
                              <TableRow
                                  hover
                                  onClick={(event) => handleClick(event, cat.masterCategoryId)}
                                  role="checkbox"
                                  aria-checked={isItemSelected}
                                  tabIndex={-1}
                                  key={cat.masterCategoryId}
                                  selected={isItemSelected}
                              >
                                <TableCell padding="checkbox">
                                  <Checkbox checked={isItemSelected} />
                                </TableCell>
                                <TableCell>{cat.masterCategoryId}</TableCell>
                                <TableCell>{cat.languageName ? languageOptions.find(option => option.code === cat.languageName)?.label : ''}</TableCell>
                                <TableCell>{cat.productId}</TableCell>
                                <TableCell className="text-overflow">
                                  <Tooltip title={cat.categoryName}>
                                    <span>{cat.categoryName}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-overflow">
                                  <Tooltip title={cat.categoryText}>
                                    <span>{cat.categoryText}</span>
                                  </Tooltip>
                                </TableCell>
                                <TableCell className="text-overflow">{renderIdsWithTooltip(cat.categoryId)}</TableCell>
                                <TableCell>
                                  <Tooltip title={<img src={cat.cgThumbnail} alt="Large Thumbnail" width="200" />}>
                                    <img className="thumbnail-image" src={cat.cgThumbnail} alt="Thumbnail" width="50" />
                                  </Tooltip>
                                </TableCell>
                                <TableCell>{cat.displayOrder}</TableCell>
                                <TableCell>
                                  <Chip label={cat.adult?.toLowerCase() === 'yes' ? 'Yes' : 'No'} color={cat.adult?.toLowerCase() === 'yes' ? 'error' : 'success'} size="small" />
                                </TableCell>
                                <TableCell className="text-overflow">{renderIdsWithTooltip(cat.carouselCid)}</TableCell>
                                <TableCell className="text-overflow">{renderIdsWithTooltip(cat.recentVideoCid)}</TableCell>
                                <TableCell>
                                  {cat.isShort === null || cat.isShort === undefined ? (
                                      '-'
                                  ) : (
                                      <Chip
                                          label={
                                            cat.isShort === 1 ||
                                            cat.isShort === true ||
                                            cat.isShort === '1'
                                                ? 'Yes'
                                                : 'No'
                                          }
                                          color={
                                            cat.isShort === 1 ||
                                            cat.isShort === true ||
                                            cat.isShort === '1'
                                                ? 'success'
                                                : 'default'
                                          }
                                          size="small"
                                      />
                                  )}
                                </TableCell>

                                <TableCell className="text-overflow">
                                  <Tooltip title={getRenderableValue(cat.shortsCidContextMapping)}>
                            <span>
                              {getRenderableValue(cat.shortsCidContextMapping) || '-'}
                            </span>
                                  </Tooltip>
                                </TableCell>

                                <TableCell>
                                  {typeof cat.circularCategoryThumbnail === 'string' &&
                                  cat.circularCategoryThumbnail.trim() !== '' ? (
                                      <Tooltip
                                          title={
                                            <img
                                                src={cat.circularCategoryThumbnail}
                                                alt="Circular Thumbnail"
                                                width="100"
                                            />
                                          }
                                      >
                                        <img
                                            className="thumbnail-image"
                                            src={cat.circularCategoryThumbnail}
                                            alt="Circular Thumbnail"
                                            width="50"
                                        />
                                      </Tooltip>
                                  ) : (
                                      '-'
                                  )}
                                </TableCell>

                                <TableCell className="actions-cell">
                                  <IconButton size="small" onClick={(event) => { event.stopPropagation(); openForm(cat); }} title="Edit Category"><Edit /></IconButton>
                                  <IconButton size="small" onClick={(event) => { event.stopPropagation(); confirmDeleteCategory(cat.masterCategoryId); }} title="Delete Category"><Delete /></IconButton>
                                </TableCell>
                              </TableRow>
                          );
                        })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredCategories.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </TableContainer>
            </Box>
        )}

        <Dialog open={openDialog} onClose={closeForm} fullWidth maxWidth="md">
          <DialogTitle>{currentCategory ? masterCategoryTranslations.editCategoryTitle : masterCategoryTranslations.addNewCategoryTitle}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} sm={6}>
                <select
                    id="language_name"
                    name="language_name"
                    value={formData.language_name}
                    onChange={handleInputChange}
                    required
                    disabled={!!currentCategory}
                    style={{
                      width: '100%',
                      height: '56px',
                      padding: '10px',
                      borderRadius: '4px',
                      border: '1px solid #ddd',
                      backgroundColor: '#f5f5f5',
                      fontSize: '16px',
                      color: formData.language_name ? '#333' : '#aaa',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      outline: 'none',
                      backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"%23333\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><polyline points=\"6 9 12 15 18 9\"/></svg>")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 10px center',
                      cursor: 'pointer',
                    }}
                >
                  <option value="" style={{ color: '#aaa' }}>{masterCategoryTranslations.languageLabel}</option>
                  {languageOptions.map(option => (
                      <option key={option.code} value={option.code}>
                        {option.label}
                      </option>
                  ))}
                </select>
                {errors.language_name && (
                    <Typography variant="caption" color="error">
                      {errors.language_name}
                    </Typography>
                )}
                {!errors.language_name && (
                    <Typography variant="caption" color="textSecondary">
                      {masterCategoryTranslations.languageHelperText || "Select a language from the dropdown"}
                    </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.productIdLabel}
                    name="product_id"
                    value={formData.product_id}
                    onChange={handleInputChange}
                    type="number"
                    helperText={errors.product_id || "Enter the product ID"}
                    error={!!errors.product_id}
                    required
                    disabled={!!currentCategory}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.categoryNameLabel}
                    name="category_name"
                    value={formData.category_name}
                    onChange={handleInputChange}
                    helperText={errors.category_name || "Enter the category name"}
                    error={!!errors.category_name}
                    required
                    disabled={!!currentCategory}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.categoryTextLabel}
                    name="category_text"
                    value={formData.category_text}
                    onChange={handleInputChange}
                    helperText={errors.category_text || "Enter a brief description of the category"}
                    error={!!errors.category_text}
                    required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.categoryIdsLabel}
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    helperText={errors.category_id || "Enter category IDs separated by commas"}
                    error={!!errors.category_id}
                    required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.carouselCidsLabel}
                    name="carousel_cid"
                    value={formData.carousel_cid}
                    onChange={handleInputChange}
                    disabled={formData.is_short}
                    helperText={
                      formData.is_short
                          ? "Disabled when Is Short is selected"
                          : "Enter carousel CIDs separated by commas"
                    }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.recentVideoCidsLabel}
                    name="recent_video_cid"
                    value={formData.recent_video_cid}
                    onChange={handleInputChange}
                    disabled={formData.is_short}
                    helperText={
                      formData.is_short
                          ? "Disabled when Is Short is selected"
                          : "Enter recent video CIDs separated by commas"
                    }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={formData.is_short}
                          onChange={handleIsShortChange}
                          name="is_short"
                      />
                    }
                    label={masterCategoryTranslations.isShortLabel}
                />
              </Grid>
              {formData.is_short && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={masterCategoryTranslations.shortsCidContextMappingLabel}
                        name="shorts_cid_context_mapping"
                        value={formData.shorts_cid_context_mapping}
                        onChange={handleInputChange}
                        helperText="Example: 123:10,456:20"
                    />
                  </Grid>
              )}
              {formData.is_short && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2">
                      {masterCategoryTranslations.circularCategoryThumbnailLabel}
                    </Typography>

                    <Input
                        type="file"
                        onChange={handleCircularThumbnailChange}
                        accept="image/*"
                    />

                    {circularThumbnailPreview && (
                        <Box mt={1} display="flex" alignItems="center">
                          <img
                              src={circularThumbnailPreview}
                              alt="Circular Thumbnail Preview"
                              width="100"
                              style={{ objectFit: 'cover' }}
                          />
                        </Box>
                    )}
                  </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Input
                    type="file"
                    onChange={handleFileChange}
                    fullWidth
                    id="image"
                    name="image"
                    accept="image/*"
                    error={!!errors.cg_thumbnail}
                    inputRef={fileInputRef}
                />
                {errors.cg_thumbnail && (
                    <Typography variant="caption" color="error">
                      {errors.cg_thumbnail}
                    </Typography>
                )}
                {imagePreview && (
                    <Box mt={1} display="flex" alignItems="center">
                      <img src={imagePreview} alt="Preview" width="100" style={{ marginRight: '10px' }} />
                      <Button variant="outlined" onClick={() => {
                        setFormData(prev => ({ ...prev, cg_thumbnail: null }));
                        setImagePreview(null);
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}>Remove</Button>
                    </Box>
                )}
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                    fullWidth
                    label={masterCategoryTranslations.displayOrderLabel}
                    name="display_order"
                    value={formData.display_order}
                    onChange={handleInputChange}
                    type="number"
                    helperText={errors.display_order || "Enter the display order"}
                    error={!!errors.display_order}
                    required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControlLabel
                    control={
                      <Checkbox
                          checked={formData.adult}
                          onChange={handleInputChange}
                          name="adult"
                          disabled={!!currentCategory}
                      />
                    }
                    label={masterCategoryTranslations.adultContentLabel}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeForm} disabled={submitting}>{masterCategoryTranslations.cancelButton}</Button>
            <Button variant="contained" onClick={submitForm} disabled={submitting}>
              {submitting ? <CircularProgress size={24} /> : currentCategory ? masterCategoryTranslations.saveButton : masterCategoryTranslations.addButton}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>{masterCategoryTranslations.categoryDeleteConfirm}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
              {masterCategoryTranslations.cancelButton}
            </Button>
            <Button onClick={deleteCategory} color="error">
              {masterCategoryTranslations.deleteButton}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
  );
};

export default withSnackbar(MasterCategory);
